import CardSystem from '../../Card/CardSystem';
import { UIEvent } from 'System/UI/BaseUISystem';
import {
    AwaitIterators,
    PlayAnimation, StartAnimationClip,
} from 'System/Animation/AnimationHelper';
import { Easing, Interpolation } from '@tweenjs/tween.js';
import TransformComponent from 'Component/Transform';
import { MainAnim } from 'System/Animation/Clip/MainAnim';
import { Alpha, DragonbonesFadeIn } from 'System/Animation/Clip';
import Point from 'Utility/Point';
import BaseCard from 'Prototype/Level/Card/BaseCard';
import Renderable from 'Component/Renderable';
import Interactable from 'Component/Interactable';
import CardComponent from 'Component/Level/Card';
import DragonbonesComponent from 'Component/Dragonbones';
import SpriteComponent from 'Component/Render/Sprite';
import ITask from 'Utility/Task/ITask';
import PlinkoCard from 'Prototype/Level/Card/PlinkoCard';
import { RenderSystemEvent } from 'System/Render/RenderSystem';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import Game from 'Game';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { PlayerActions } from 'State/Player/PlayerActions';
import { PlaySound } from 'System/Audio/AudioHelper';
import { GameObjectEvent } from 'GameObject/GameObject';

export class RevealPlinkoCardTask implements ITask<CardSystem> {
    public *execute(system: CardSystem): IterableIterator<number> {
        system.scene.emit(UIEvent.DISABLE_INTERACTIVITY, false);

        const animator = system.animator;
        const animatorTransform = animator.getComponent(TransformComponent);

        const center = new Point(animatorTransform.width / 2, animatorTransform.height / 2);

        const cardsState = system.store.getState().levelState.cardsState;

        const plinko = system.getCardsInPlay().find(card => card instanceof PlinkoCard) as PlinkoCard;

        if (!plinko) return;

        const plinkoTransform = plinko.getComponent(TransformComponent);
        const plinkoCardComponent = plinko.getComponent(CardComponent);
        const plinkoArmature = plinko.getComponentsInChildren(DragonbonesComponent)?.find(db => db.armatureName === 'card').gameObject;
        const plinkoParent = plinko.parent;

        const plinkoInitialPosition = plinkoTransform.position.clone();
        const plinkoInitialRotation = plinkoTransform.rotation;

        system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, animator);
        animator.addChild(plinko);
        plinkoTransform.position.set(center.x, center.y - 80);
        plinkoTransform.rotation = 0;

        plinko.getComponent(Renderable).alpha = 1;
        plinko.getComponent(Interactable).enabled = false;
        plinko.getChildByName(BaseCard.CARD_FRONT_NAME).getComponent(SpriteComponent).visible = false;
        plinko.getChildByName(BaseCard.CARD_BACK_NAME).getComponent(SpriteComponent).visible = false;
        plinkoCardComponent.animating = true;

        const state = Game.store.getState();
        const haveFTUX = !FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.PLINKO_CARD);
        if (haveFTUX) {
            // Show plinko ftux
            const ftuxOverlay = plinko.getLayout().ftuxOverlay;
            const ftuxText = plinko.getLayout().ftuxText;
            [ftuxOverlay, ftuxText].forEach(ftux => {
                StartAnimationClip(
                    new Alpha(ftux, {
                        duration: 300,
                        startAlpha: 0,
                        endAlpha: 1,
                    })
                );
            });
        }

        PlaySound('plinko-card-appear');

        yield* PlayAnimation(
            new DragonbonesFadeIn(plinkoArmature, {
                playTimes: 1,
                animationName: 'appear',
                fadeInTime: 50,
                concat: false,
            })
        );

        if (haveFTUX) {
            plinko.container.interactiveChildren = true;

            const ftuxOverlay = plinko.getLayout().ftuxOverlay;
            const ftuxText = plinko.getLayout().ftuxText;

            const ftuxInteractable = ftuxText.getComponent(Interactable);
            ftuxInteractable.buttonMode = true;
            ftuxInteractable.enabled = true;

            system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, animator);

            let endTutorial = false;
            ftuxText.on(GameObjectEvent.SELECTED, () => {
                endTutorial = true;
            });

            while (!endTutorial) {
                yield;
            }

            PlayerActions.setFTUXUnlocks([FTUXType.PLINKO_CARD]);

            plinko.container.interactiveChildren = true;

            ftuxInteractable.buttonMode = false;
            ftuxInteractable.enabled = false;

            // Hide plinko ftux
            const toHide: IterableIterator<number>[] = [];
            [ftuxOverlay, ftuxText].forEach(ftux => {
                toHide.push(
                    PlayAnimation(
                        new Alpha(ftux, {
                            duration: 300,
                            startAlpha: 1,
                            endAlpha: 0,
                        })
                    ));
            });
            yield* AwaitIterators(toHide)

        }

        plinkoParent.addChild(plinko);

        yield* PlayAnimation(
            new MainAnim(
                plinko,
                {
                    x: plinkoInitialPosition.x,
                    y: plinkoInitialPosition.y - 170,
                    scaleX: 1,
                    scaleY: 1,
                    duration: 400,
                    rotation: plinkoInitialRotation,
                    easing: Easing.Sinusoidal.Out,
                    interpolation: Interpolation.Bezier,
                },
                true
            )
        );

        plinko.getChildByName(BaseCard.CARD_FRONT_NAME).getComponent(SpriteComponent).visible = true;
        plinko.getChildByName(BaseCard.CARD_BACK_NAME).getComponent(SpriteComponent).visible = true;
        plinkoArmature.getComponent(Renderable).visible = false;
        plinkoArmature.getComponent(Renderable).alpha = 0;

        system.sortCardsInPlay(cardsState);

        yield* PlayAnimation(
            new MainAnim(
                plinko,
                {
                    y: plinkoInitialPosition.y,
                    duration: 250,
                },
                true
            )
        );

        plinkoCardComponent.animating = false;

        system.scene.emit(UIEvent.RESTORE_INTERACTIVITY, true);
        return;
    }
}
